import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { UserRoleService } from './role.service';
import { TranslateService } from '@ngx-translate/core';


export interface DateFormatConfig {
    locale: string;
    matDateLocale: string;
    displayFormat: string;
    parseFormat: string;
    timezone: string;
}


export const COUNTRY_DATE_FORMATS: Record<string, { displayFormat: string; parseFormat: string; timezone: string }> = {
    ES: {
        displayFormat: 'dd/MM/yyyy',
        parseFormat: 'dd/MM/yyyy',
        timezone: 'Europe/Madrid',
    },
    FR: {
        displayFormat: 'dd/MM/yyyy',
        parseFormat: 'dd/MM/yyyy',
        timezone: 'Europe/Paris',
    },
    IT: {
        displayFormat: 'dd/MM/yyyy',
        parseFormat: 'dd/MM/yyyy',
        timezone: 'Europe/Rome',
    },
    PT: {
        displayFormat: 'dd/MM/yyyy',
        parseFormat: 'dd/MM/yyyy',
        timezone: 'Europe/Lisbon',
    },
    DE: {
        displayFormat: 'dd.MM.yyyy',
        parseFormat: 'dd.MM.yyyy',
        timezone: 'Europe/Berlin',
    },
    GB: {
        displayFormat: 'dd/MM/yyyy',
        parseFormat: 'dd/MM/yyyy',
        timezone: 'Europe/London',
    },
    US: {
        displayFormat: 'MM/dd/yyyy',
        parseFormat: 'MM/dd/yyyy',
        timezone: 'America/New_York',
    },
    CL: {
        displayFormat: 'dd/MM/yyyy',
        parseFormat: 'dd/MM/yyyy',
        timezone: 'America/Santiago',
    },
    CR: {
        displayFormat: 'dd/MM/yyyy',
        parseFormat: 'dd/MM/yyyy',
        timezone: 'America/Costa_Rica',
    },
    TW: {
        displayFormat: 'yyyy/MM/dd',
        parseFormat: 'yyyy/MM/dd',
        timezone: 'Asia/Taipei',
    },
};

const LANGUAGE_TO_MAT_LOCALE: Record<string, string> = {
    'en': 'en-GB',
    'es': 'es-ES',
    'fr': 'fr-FR',
    'de': 'de-DE',
    'it': 'it-IT',
    'pt': 'pt-PT',
    'cht': 'zh-TW',
};

export const DEFAULT_DATE_FORMAT = COUNTRY_DATE_FORMATS['ES'];


@Injectable({
    providedIn: 'root',
})
export class DateFormatLocalizationService {
    private readonly datePipe = new DatePipe('en-US');
    private currentCountryCode: string = 'ES';
    private currentLanguage: string = 'en';


    private readonly language$: Observable<string> = this.translateService.onLangChange.pipe(
        map(event => event.lang),
        startWith(this.translateService.currentLang || localStorage.getItem('language') || 'en'),
        distinctUntilChanged()
    );

    private readonly effectiveCountry$: Observable<string> = combineLatest([
        this.userRoleService.currentUser$.pipe(startWith(null)),
        this.userRoleService.centerCountry$.pipe(startWith(null))
    ]).pipe(
        map(([user, centerCountry]) => user?.country || centerCountry || 'ES'),
        distinctUntilChanged()
    );

    readonly dateFormatConfig$: Observable<DateFormatConfig> = combineLatest([
        this.language$,
        this.effectiveCountry$
    ]).pipe(
        map(([lang, country]) => this.buildConfig(lang || 'en', country || 'ES'))
    );

    constructor(
        private readonly userRoleService: UserRoleService,
        private readonly translateService: TranslateService
    ) {
        this.currentLanguage = this.translateService.currentLang || localStorage.getItem('language') || 'en';

        this.language$.subscribe(lang => {
            this.currentLanguage = lang || 'en';
        });

        this.effectiveCountry$.subscribe(country => {
            this.currentCountryCode = this.countryNameToCode(country || 'ES');
        });
    }

    private buildConfig(language: string, countryNameOrCode: string): DateFormatConfig {
        const countryCode = this.countryNameToCode(countryNameOrCode);
        const formatConfig = COUNTRY_DATE_FORMATS[countryCode] || DEFAULT_DATE_FORMAT;
        const matLocale = this.languageToMatLocale(language);
        const fullLocale = this.languageToFullLocale(language);


        return {
            locale: fullLocale,
            matDateLocale: matLocale,
            displayFormat: formatConfig.displayFormat,
            parseFormat: formatConfig.parseFormat,
            timezone: formatConfig.timezone,
        };
    }

    getCurrentConfig(): DateFormatConfig {
        return this.buildConfig(this.currentLanguage, this.currentCountryCode);
    }


    formatDate(date: Date | string | null | undefined): string {
        if (!date) return '';
        const dateObj = typeof date === 'string' ? this.parseDate(date) : date;
        if (!dateObj || isNaN(dateObj.getTime())) {
            return '';
        }
        const config = this.getCurrentConfig();
        return this.datePipe.transform(dateObj, config.displayFormat) ?? '';
    }

    parseDate(dateString: string | null | undefined): Date | null {
        if (!dateString) return null;

        const trimmed = dateString.trim();
        if (!trimmed) return null;

        const isoDateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
        if (isoDateOnlyMatch) {
            const year = parseInt(isoDateOnlyMatch[1], 10);
            const month = parseInt(isoDateOnlyMatch[2], 10);
            const day = parseInt(isoDateOnlyMatch[3], 10);
            return new Date(year, month - 1, day);
        }

        const isoDate = new Date(trimmed);
        if (!isNaN(isoDate.getTime())) {
            return isoDate;
        }

        const config = this.getCurrentConfig();
        return this.parseDateByFormat(trimmed, config.parseFormat);
    }

    getDisplayFormatLabel(): string {
        return this.getCurrentConfig().displayFormat.toUpperCase();
    }

    getMatDateLocale(): string {
        return this.languageToMatLocale(this.currentLanguage);
    }

    getTimezone(): string {
        return this.getCurrentConfig().timezone;
    }

    getCurrentCountryCode(): string {
        return this.currentCountryCode || 'ES';
    }

    private languageToMatLocale(language: string): string {
        return LANGUAGE_TO_MAT_LOCALE[language] || 'en-GB';
    }

    private languageToFullLocale(language: string): string {
        return LANGUAGE_TO_MAT_LOCALE[language] || 'en-GB';
    }

    private countryNameToCode(countryNameOrCode: string): string {
        if (!countryNameOrCode) return 'ES';

        const normalized = countryNameOrCode.trim().toUpperCase();

        if (/^[A-Z]{2}$/.test(normalized)) {
            return COUNTRY_DATE_FORMATS[normalized] ? normalized : 'ES';
        }

        const nameToCodeMap: Record<string, string> = {
            ESPAÑA: 'ES',
            ESPANA: 'ES',
            SPAIN: 'ES',
            FRANCE: 'FR',
            FRANCIA: 'FR',
            ITALY: 'IT',
            ITALIA: 'IT',
            GERMANY: 'DE',
            ALEMANIA: 'DE',
            PORTUGAL: 'PT',
            PORTUGUES: 'PT',
            PORTUGUÊS: 'PT',
            'UNITED KINGDOM': 'GB',
            'REINO UNIDO': 'GB',
            REINOUNIDO: 'GB',
            'UNITED STATES': 'US',
            'ESTADOS UNIDOS': 'US',
            ESTADOSUNIDOS: 'US',
            CHILE: 'CL',
            'COSTA RICA': 'CR',
            'COSTA ROCA': 'CR',
            COSTARICA: 'CR',
            COSTRICA: 'CR',
            TAIWAN: 'TW',
            'TAIWÁN': 'TW',
            'ESTADOS UNIDO': 'US',
        };

        return nameToCodeMap[normalized] ?? 'ES';
    }

    private parseDateByFormat(dateString: string, format: string): Date | null {
        const delimiter = /[/.\-]/.exec(format)?.[0] || '/';
        const parts = dateString.split(delimiter).map(p => p.trim());

        const formatParts = format.toLowerCase().split(delimiter).map(p => p.trim());
        const dayIndex = formatParts.findIndex(token => token.includes('dd'));
        const monthIndex = formatParts.findIndex(token => token.includes('mm'));
        const yearIndex = formatParts.findIndex(token => token.includes('yyyy'));

        if (
            parts.length !== 3 ||
            formatParts.length !== 3 ||
            dayIndex < 0 ||
            monthIndex < 0 ||
            yearIndex < 0
        ) {
            return null;
        }

        const day = parseInt(parts[dayIndex] ?? '0', 10);
        const month = parseInt(parts[monthIndex] ?? '0', 10);
        const year = parseInt(parts[yearIndex] ?? '0', 10);

        if (isNaN(day) || isNaN(month) || isNaN(year) || month < 1 || month > 12 || day < 1 || day > 31) {
            return null;
        }

        const date = new Date(year, month - 1, day);
        return date.getMonth() === month - 1 && date.getDate() === day ? date : null;
    }
}
